// v10/src/components/admin/AdminInspections.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardCheck, Search, ChevronDown, ChevronUp,
  CheckCircle2, AlertTriangle, Clock, Loader, X, Calendar,
  User, TrendingUp, BarChart2, FileDown
} from 'lucide-react';
import { inspectionService } from '../../services/apiService';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/useToast';

const STATUS_CONFIG = {
  completed:  { label: 'Completada', color: 'green',  Icon: CheckCircle2 },
  incomplete: { label: 'Incompleta', color: 'yellow', Icon: AlertTriangle },
  pending:    { label: 'Pendiente',  color: 'gray',   Icon: Clock },
};

// inspection_date llega del backend como columna `date` de Postgres, que pg
// serializa como ISO completo ("2026-08-19T00:00:00.000Z"), no como
// "2026-08-19" plano. Partir en 'T' antes de leer año/mes/día evita tanto
// el "Invalid Date" (de concatenarle otro "T00:00:00" a un string que ya
// tiene uno) como el corrimiento de día por timezone de `new Date(...)`.
const formatInspectionDate = (value) => {
  if (!value) return '—';
  const parts = String(value).split('T')[0].split('-');
  if (parts.length !== 3) return '—';
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const { Icon } = cfg;
  const colors = {
    green:  'bg-green-100 text-green-700 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    gray:   'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${colors[cfg.color]}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color, darkMode }) => {
  const colors = {
    blue:   { bg: darkMode ? 'bg-blue-900/30'   : 'bg-blue-50',   icon: 'bg-blue-500' },
    green:  { bg: darkMode ? 'bg-green-900/30'  : 'bg-green-50',  icon: 'bg-green-500' },
    yellow: { bg: darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50', icon: 'bg-yellow-500' },
    purple: { bg: darkMode ? 'bg-purple-900/30' : 'bg-purple-50', icon: 'bg-purple-500' },
  };
  const c = colors[color] || colors.blue;
  return (
    <motion.div className={`${c.bg} rounded-2xl p-4 border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`} whileHover={{ scale: 1.02 }}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`${c.icon} p-2 rounded-xl`}><Icon className="w-4 h-4 text-white" /></div>
        <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</p>
      </div>
      <p className={`text-3xl font-black ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
    </motion.div>
  );
};

// ── Fases (Sub-etapa 3.3) ───────────────────────────────────────────
const PHASE_LABELS = {
  prevuelo:          'Prevuelo',
  puesta_en_marcha:  'Puesta en marcha',
  prueba_motor:      'Prueba de motor',
};

const PHASE_STATUS_CONFIG = {
  completed:   { label: 'Completada', color: 'green'  },
  incomplete:  { label: 'Incompleta', color: 'yellow' },
  abandoned:   { label: 'Salteada',   color: 'gray'   },
  in_progress: { label: 'En curso',   color: 'blue'   },
};

// Sub-etapa 3.4 — abreviaturas para la columna "Fases" del PDF gerencial
// (espacio acotado, no entran los labels completos de PHASE_LABELS/
// PHASE_STATUS_CONFIG). Solo se usan ahí; el CSV reutiliza los labels
// completos de PHASE_STATUS_CONFIG.
const PHASE_LABELS_SHORT = {
  prevuelo:          'Prev',
  puesta_en_marcha:  'P.Mar',
  prueba_motor:      'P.Mot',
};
const PHASE_STATUS_SHORT = {
  completed:   'OK',
  incomplete:  'INC',
  abandoned:   'SALT',
  in_progress: 'CUR',
};

const PhaseStatusBadge = ({ status }) => {
  const cfg = PHASE_STATUS_CONFIG[status] || { label: status, color: 'gray' };
  const colors = {
    green:  'bg-green-100 text-green-700 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    gray:   'bg-gray-100 text-gray-600 border-gray-200',
    blue:   'bg-blue-100 text-blue-700 border-blue-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors[cfg.color]}`}>
      {cfg.label}
    </span>
  );
};

// Indicador compacto de fases en la fila colapsada (Parte C del brief:
// "un indicador — ej. '3 fases' — o los badges de las fases").
const PhasesSummary = ({ phases, darkMode }) => {
  if (!phases || phases.length < 2) return null;
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {phases.map(p => (
        <span key={p.id} title={PHASE_LABELS[p.phase] || p.phase}
          className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
            p.status === 'completed'  ? (darkMode ? 'bg-green-900/40 text-green-400'  : 'bg-green-100 text-green-700') :
            p.status === 'abandoned'  ? (darkMode ? 'bg-gray-700 text-gray-400'       : 'bg-gray-100 text-gray-500') :
                                         (darkMode ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
          }`}>
          {(PHASE_LABELS[p.phase] || p.phase).split(' ')[0]}
        </span>
      ))}
    </span>
  );
};

// ── Bloque de ítems de un checklist (por sección, con progreso) ────
// Extraído de InspectionDetail para reusarlo también por fase dentro de
// GroupPhasesDetail (Sub-etapa 3.3) — no duplicar el mismo JSX dos veces.
const ChecklistItemsBlock = ({ items, remarks, darkMode }) => {
  const total   = items.length;
  const checked = items.filter(i => i.status === 'checked').length;
  const skipped = items.filter(i => i.status === 'skipped').length;
  const pct     = total ? Math.round((checked / total) * 100) : 0;

  const bySection = items.reduce((acc, item) => {
    if (!acc[item.section_name]) acc[item.section_name] = [];
    acc[item.section_name].push(item);
    return acc;
  }, {});

  return (
    <>
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Progreso del chequeo</span>
          <span className="font-bold text-blue-600">{pct}%</span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
          <motion.div className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
            initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
        </div>
        <div className="flex gap-4 mt-2 text-xs">
          <span className="text-green-600">✓ {checked} chequeados</span>
          {skipped > 0 && <span className="text-yellow-600">⚠ {skipped} salteados</span>}
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{total} total</span>
        </div>
      </div>

      {Object.entries(bySection).map(([section, sItems]) => (
        <div key={section} className="mb-3">
          <p className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{section}</p>
          <div className="space-y-1">
            {sItems.map(item => (
              <div key={item.id} className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {item.status === 'checked' && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                {item.status === 'skipped' && <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />}
                {item.status === 'pending' && <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                <span className={item.status === 'skipped' ? 'line-through opacity-60' : ''}>{item.item_text}</span>
                {item.notes && <span className={`text-xs ml-1 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>— {item.notes}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {remarks && (
        <div className={`mt-3 p-3 rounded-xl text-sm ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-600'}`}>
          <span className="font-semibold">Observaciones: </span>{remarks}
        </div>
      )}
    </>
  );
};

// ── Detalle de inspección suelta (group_id NULL, comportamiento pre-3.3) ─
const InspectionDetail = ({ id, darkMode }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // ✅ FIX: el backend devuelve { success, data: { ...inspection, items } }
    // r.data es la respuesta axios → r.data.data es el objeto real con items
    inspectionService.getById(id)
      .then(r => { setData(r.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin text-blue-500" /></div>;
  if (!data)   return null;

  return (
    <div className={`mt-4 p-4 rounded-2xl border ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-100 bg-gray-50'}`}>
      <ChecklistItemsBlock items={data.items || []} remarks={data.remarks} darkMode={darkMode} />
    </div>
  );
};

// ── Detalle de un chequeo multi-fase (contenedor) — Sub-etapa 3.3 ──
// Un solo viaje a GET /api/inspections/groups/:group_id (no N llamados a
// getInspectionById) que trae las hasta 3 fases con sus ítems.
const GroupPhasesDetail = ({ groupId, darkMode }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    inspectionService.getGroup(groupId)
      .then(r => { setData(r.data.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [groupId]);

  if (loading) return <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin text-blue-500" /></div>;
  if (!data)   return null;

  return (
    <div className="mt-4 space-y-3">
      {data.phases.map(phase => (
        <div key={phase.id} className={`p-4 rounded-2xl border ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-100 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-3">
            <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {PHASE_LABELS[phase.phase] || phase.phase}
            </p>
            <PhaseStatusBadge status={phase.status} />
          </div>
          {phase.status === 'abandoned' ? (
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Fase opcional salteada por el piloto — no tiene ítems registrados.
            </p>
          ) : (
            <ChecklistItemsBlock items={phase.items || []} remarks={phase.remarks} darkMode={darkMode} />
          )}
        </div>
      ))}
    </div>
  );
};

// ── CSV Export ────────────────────────────────────────────────────
// Sub-etapa 3.4 — orden fijo de fases para las columnas repetidas del CSV
// (mismas 3 fases que PHASE_LABELS/PHASE_STATUS_CONFIG más arriba).
const CSV_PHASE_KEYS = ['prevuelo', 'puesta_en_marcha', 'prueba_motor'];

const exportToCSV = (rows) => {
  const headers = [
    'Fecha', 'Hora', 'Aeronave', 'Matrícula', 'Piloto', 'Estado', 'Ítems chequeados', 'Total ítems', 'Completado %',
    // Columnas de fase repetidas (Sub-etapa 3.4) — "1 fila = 1 chequeo" se
    // mantiene. Chequeos de 1 fase (la mayoría de Madryn): solo se llena
    // "Estado Prevuelo"/"Ítems Prevuelo", el resto queda vacío.
    'Estado Prevuelo', 'Ítems Prevuelo',
    'Estado Puesta en marcha', 'Ítems Puesta en marcha',
    'Estado Prueba motor', 'Ítems Prueba motor',
  ];
  const csvRows = [
    headers.join(';'),
    ...rows.map(r => {
      const total   = r.total_items   || 0;
      const checked = r.checked_items || 0;
      const pct     = total ? Math.round((checked / total) * 100) : 0;

      // Fase presente en este chequeo → [Estado, "checked/total"]; ausente
      // (chequeo de 1 fase, o esa fase nunca se alcanzó) → columnas vacías.
      const phaseCols = CSV_PHASE_KEYS.flatMap(key => {
        const p = (r.phases || []).find(ph => ph.phase === key);
        if (!p) return ['', ''];
        return [PHASE_STATUS_CONFIG[p.status]?.label || p.status, `${p.checked_items || 0}/${p.total_items || 0}`];
      });

      return [
        formatInspectionDate(r.inspection_date),
        r.inspection_time || '',
        `${r.manufacturer || ''} ${r.model_name || ''}`.trim(),
        r.registration || '',
        r.inspector_name || '',
        r.status === 'completed' ? 'Completada' : 'Incompleta',
        checked, total, `${pct}%`,
        ...phaseCols,
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';');
    }),
  ];
  const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `inspecciones_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Main ──────────────────────────────────────────────────────────
const AdminInspections = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [inspections, setInspections] = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [expandedId,  setExpandedId]  = useState(null);

  // ✅ search separado de filters: se debouncea antes de ir al backend
  const [search,      setSearch]      = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  const [filters,     setFilters]     = useState({ status: '', date_from: '', date_to: '' });
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [total,       setTotal]       = useState(0);
  const [exporting,   setExporting]   = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Debounce del search: 400ms tras dejar de escribir
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ✅ search se envía al backend como parámetro
      // grouped=true (Sub-etapa 3.3): 1 fila = 1 chequeo (contenedor),
      // no 1 fila por fase — ver 3.3.md.
      const params = { page, limit: 15, grouped: 'true', ...filters };
      if (debouncedSearch) params.search = debouncedSearch;

      const [inspRes, statsRes] = await Promise.all([
        inspectionService.getAll(params),
        inspectionService.getStats(),
      ]);
      setInspections(inspRes.data.data || []);
      setTotalPages(inspRes.data.pages || 1);
      setTotal(inspRes.data.count || inspRes.data.total || 0);
      setStats(statsRes.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, filters, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleExportPDF = useCallback(async () => {
    setExportingPDF(true);
    try {
      // Traer todos los registros con los filtros activos
      // grouped=true (Sub-etapa 3.3): igual que la lista, 1 fila = 1 chequeo.
      const params = { page: 1, limit: 9999, grouped: 'true', ...filters };
      if (debouncedSearch) params.search = debouncedSearch;
      const [inspRes, statsRes] = await Promise.all([
        inspectionService.getAll(params),
        inspectionService.getStats(),
      ]);
      const rows  = inspRes.data.data   || [];
      const stats = statsRes.data.data  || {};

      if (!rows.length) { toast.info('No hay inspecciones para exportar.'); return; }

      const pdf  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const W    = pdf.internal.pageSize.getWidth();
      const H    = pdf.internal.pageSize.getHeight();

      // ── Paleta idéntica a AdminHobbsReport ────────────────────
      const NAVY   = [15,  40,  80];
      const BLUE   = [37,  99,  235];
      const PURPLE = [124, 58,  237];
      const GREEN  = [22,  163, 74];
      const RED    = [239, 68,  68];
      const AMBER  = [217, 119, 6];
      const GRAY   = [100, 116, 139];
      const LGRAY  = [241, 245, 249];
      const BLACK  = [15,  23,  42];
      const WHITE  = [255, 255, 255];

      const setColor = (rgb) => pdf.setTextColor(...rgb);
      const setFill  = (rgb) => pdf.setFillColor(...rgb);
      const setDraw  = (rgb) => pdf.setDrawColor(...rgb);
      const bold     = (s = 10) => { pdf.setFont('helvetica', 'bold');   pdf.setFontSize(s); };
      const normal   = (s = 9)  => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(s); };
      const small    = (s = 7)  => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(s); };

      const generatedFull = new Date().toLocaleString('es-AR', { dateStyle: 'long', timeStyle: 'short' });

      const addPageHeader = () => {
        // Header — exactamente igual al reporte mensual
        setFill(NAVY); pdf.rect(0, 0, W, 22, 'F');
        setFill(BLUE); pdf.circle(16, 11, 6, 'F');
        bold(8);  setColor(WHITE); pdf.text('C360', 16, 13, { align: 'center' });
        bold(13); setColor(WHITE); pdf.text('CONDOR360', 26, 9);
        normal(7); setColor([147, 197, 253]); pdf.text('Aeroclubes y Escuelas de Aviación', 26, 14);
        bold(10);  setColor(WHITE); pdf.text('Reporte de Inspecciones Prevuelo', W / 2, 9,  { align: 'center' });
        small(7);  setColor([147, 197, 253]); pdf.text(`Generado: ${generatedFull}`, W / 2, 14, { align: 'center' });
      };

      const addPageFooter = (pageNum, totalPages) => {
        setFill(NAVY); pdf.rect(0, H - 12, W, 12, 'F');
        small(7); setColor([147, 197, 253]);
        pdf.text('Condor360 · Aeroclubes y Escuelas de Aviación', W / 2, H - 5, { align: 'center' });
        setColor([71, 114, 182]);
        const filterLabel = filters.status ? ` · ${filters.status}` : '';
        pdf.text(`Inspecciones${filterLabel}`, 14, H - 5);
        pdf.text(`Pág. ${pageNum} de ${totalPages}`, W - 14, H - 5, { align: 'right' });
      };

      // ── Página 1 ──────────────────────────────────────────────
      addPageHeader();
      let y = 26;

      // Metadatos — mismo estilo card con barra izquierda azul
      setFill(LGRAY); setDraw([226, 232, 240]);
      pdf.roundedRect(14, y, W - 28, 16, 3, 3, 'FD');
      setFill(BLUE); pdf.roundedRect(14, y, 3, 16, 1.5, 1.5, 'F');
      const colW = (W - 28 - 6) / 3;
      const filterParts = [];
      if (filters.date_from) filterParts.push(`Desde: ${filters.date_from}`);
      if (filters.date_to)   filterParts.push(`Hasta: ${filters.date_to}`);
      if (debouncedSearch)   filterParts.push(`Búsqueda: "${debouncedSearch}"`);
      [
        { label: 'ESTADO',    value: filters.status ? { completed:'Completadas', completed_with_pending:'Completadas c/ salteados', incomplete:'Incompletas', in_progress:'En curso' }[filters.status] || filters.status : 'Todos', color: BLACK },
        { label: t('filter_period'),   value: filterParts.filter(p => p.startsWith('Desde') || p.startsWith('Hasta')).join(' · ') || t('filter_no_date'), color: BLUE },
        { label: 'REGISTROS', value: `${rows.length} inspecciones`, color: PURPLE },
      ].forEach((m, i) => {
        const x = 20 + i * (colW + 2);
        small(6.5); setColor(GRAY); pdf.text(m.label, x, y + 6);
        bold(8.5);  setColor(m.color); pdf.text(m.value, x, y + 12);
      });
      y += 22;

      // Stats cards — mismo patrón que reporte mensual
      bold(11); setColor(BLACK); pdf.text('Resumen', 14, y);
      y += 2; setFill(BLUE); pdf.rect(14, y, 20, 0.8, 'F'); y += 5;
      const cardW = (W - 28 - 9) / 4;
      [
        { label: 'Total',                    value: stats.total_inspections    || 0, color: BLUE   },
        { label: 'Completadas OK',           value: stats.completed_ok         || 0, color: GREEN  },
        { label: 'Completadas c/ salteados', value: stats.completed_with_pending || 0, color: PURPLE },
        { label: 'Incompletas',              value: stats.incomplete           || 0, color: AMBER  },
      ].forEach((s, i) => {
        const x = 14 + i * (cardW + 3);
        setFill(LGRAY); setDraw([226, 232, 240]); pdf.roundedRect(x, y, cardW, 20, 3, 3, 'FD');
        setFill(s.color); pdf.roundedRect(x, y, 3, 20, 1.5, 1.5, 'F');
        bold(16); setColor(BLACK); pdf.text(String(s.value), x + 8, y + 10);
        small(6.5); setColor(s.color); pdf.text(s.label.toUpperCase(), x + 8, y + 16);
      });
      y += 28;

      // ── Tabla de inspecciones ─────────────────────────────────
      bold(11); setColor(BLACK); pdf.text('Detalle de Inspecciones', 14, y);
      y += 2; setFill(BLUE); pdf.rect(14, y, 36, 0.8, 'F'); y += 5;

      const cols = [
        { label: 'Fecha',     x: 14,  w: 22  },
        { label: 'Aeronave',  x: 38,  w: 42  },
        { label: t('filter_registration'), x: 82,  w: 22  },
        { label: 'Piloto',    x: 106, w: 46  },
        { label: 'Estado',    x: 154, w: 30  },
        { label: 'Progreso',  x: 186, w: 22  },
        { label: 'Items',     x: 210, w: 16  },
        // Sub-etapa 3.4 — desglose de fases. Usa el espacio libre hasta el
        // borde derecho de la tabla (14 a W-14 = 283mm en A4 landscape).
        { label: 'Fases',     x: 230, w: 53  },
      ];

      // Header de tabla
      setFill(BLUE); pdf.rect(14, y, W - 28, 8, 'F');
      bold(7); setColor(WHITE);
      cols.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 2, y + 5.2));
      y += 8;

      const statusLabel = (s) => ({
        completed:              'Completada',
        incomplete:             'Incompleta',
        in_progress:            'En curso',
        completed_with_pending: 'C/ salteados',
      }[s] || s);

      const statusColor = (s) => ({
        completed:   GREEN,
        incomplete:  AMBER,
        in_progress: BLUE,
      }[s] || GRAY);

      // Sub-etapa 3.4 — color de pastilla por fase (mismo criterio de
      // statusColor, más 'abandoned' en gris — fase salteada).
      const phaseStatusColor = (s) => ({
        completed:   GREEN,
        incomplete:  AMBER,
        abandoned:   GRAY,
        in_progress: BLUE,
      }[s] || GRAY);

      let page = 1;
      const rowH = 9;
      const maxY = H - 18;

      rows.forEach((insp, i) => {
        if (y + rowH > maxY) {
          addPageFooter(page, '?');
          pdf.addPage();
          page++;
          addPageHeader();
          y = 28;
          // Repetir header de tabla en nueva página
          setFill(BLUE); pdf.rect(14, y, W - 28, 8, 'F');
          bold(7); setColor(WHITE);
          cols.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 2, y + 5.2));
          y += 8;
        }

        const bg = i % 2 === 0 ? [248, 250, 252] : WHITE;
        setFill(bg); setDraw([226, 232, 240]); pdf.rect(14, y, W - 28, rowH, 'FD');

        // Fecha
        normal(7.5); setColor(BLACK);
        const dateStr = formatInspectionDate(insp.inspection_date);
        pdf.text(dateStr, cols[0].x + 2, y + 5.5);

        // Aeronave
        bold(7.5); setColor(BLACK);
        const acName = `${insp.manufacturer || ''} ${insp.model_name || ''}`.trim();
        pdf.text(acName.substring(0, 22), cols[1].x + 2, y + 5.5);

        // Matrícula
        normal(7); setColor(GRAY);
        pdf.text(insp.registration || '—', cols[2].x + 2, y + 5.5);

        // Piloto
        normal(7.5); setColor(BLACK);
        const pilotName = (insp.inspector_name || '—').substring(0, 24);
        pdf.text(pilotName, cols[3].x + 2, y + 5.5);

        // Status badge
        const sc = statusColor(insp.status);
        const sl = statusLabel(insp.status);
        const badgeW = 26, badgeH = 5.5;
        const bx = cols[4].x + 1, by = y + 1.8;
        setFill([...sc.map(v => Math.min(255, v + 185))]);
        pdf.roundedRect(bx, by, badgeW, badgeH, 2, 2, 'F');
        bold(6.5); setColor(sc);
        pdf.text(sl, bx + badgeW / 2, by + 3.8, { align: 'center' });

        // Progreso %
        const total   = insp.total_items   || 0;
        const checked = insp.checked_items || 0;
        const pct     = total ? Math.round((checked / total) * 100) : 0;
        const barW    = 18, barH = 2.5, bary = y + 3.5;
        setFill([226, 232, 240]); pdf.roundedRect(cols[5].x + 2, bary, barW, barH, 1, 1, 'F');
        if (pct > 0) {
          setFill(pct === 100 ? GREEN : BLUE);
          pdf.roundedRect(cols[5].x + 2, bary, barW * pct / 100, barH, 1, 1, 'F');
        }
        small(6.5); setColor(pct === 100 ? GREEN : GRAY);
        pdf.text(`${pct}%`, cols[5].x + 2 + barW / 2, y + 8, { align: 'center' });

        // Items checkeados
        bold(7.5); setColor(checked === total && total > 0 ? GREEN : GRAY);
        pdf.text(`${checked}/${total}`, cols[6].x + 2, y + 5.5);

        // Fases (Sub-etapa 3.4) — solo se desglosa si el chequeo tiene más
        // de 1 fase. Chequeos sueltos (la mayoría de Madryn) muestran un
        // guion, igual que las demás columnas sin dato (ver Matrícula/
        // Piloto más arriba) — no-regresión: se ven como antes de la 3.4.
        const phasesCol = cols[7];
        if (insp.phases && insp.phases.length > 1) {
          const gap    = 1;
          const badgeW = (phasesCol.w - gap * (insp.phases.length - 1)) / insp.phases.length;
          insp.phases.forEach((p, pi) => {
            const pc  = phaseStatusColor(p.status);
            const pbx = phasesCol.x + pi * (badgeW + gap);
            const pby = y + 1.8;
            setFill([...pc.map(v => Math.min(255, v + 185))]);
            pdf.roundedRect(pbx, pby, badgeW, badgeH, 1.5, 1.5, 'F');
            bold(5.5); setColor(pc);
            const label = `${PHASE_LABELS_SHORT[p.phase] || p.phase}:${PHASE_STATUS_SHORT[p.status] || p.status}`;
            pdf.text(label, pbx + badgeW / 2, pby + 3.8, { align: 'center' });
          });
        } else {
          normal(7.5); setColor(GRAY);
          pdf.text('—', phasesCol.x + phasesCol.w / 2, y + 5.5, { align: 'center' });
        }

        y += rowH;
      });

      // Actualizar footers con total de páginas real
      const totalPages = page;
      // Footer última página
      addPageFooter(page, totalPages);

      // Disclaimer referencial en todas las páginas (arriba del rect NAVY)
      const disclaimerText = 'Reporte gerencial generado por Condor360 — herramienta de asistencia administrativa. Los registros aquí listados no reemplazan los comprobantes oficiales de inspección ni los procedimientos del fabricante (POH). La responsabilidad operacional de cada vuelo es del Piloto al Mando.';
      const pageTotal = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageTotal; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(disclaimerText, W / 2, H - 16, { align: 'center', maxWidth: W - 28 });
      }
      pdf.setTextColor(0, 0, 0);

      // Volver a páginas anteriores y poner el total correcto
      // (jsPDF no permite editar páginas pasadas fácilmente,
      //  así que si hay múltiples páginas ponemos el total en el save)
      pdf.save(`Condor360_Inspecciones_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al generar el PDF. Intentá de nuevo.');
    } finally { setExportingPDF(false); }
  }, [filters, debouncedSearch, stats]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // grouped=true (Sub-etapa 3.3): igual que la lista, 1 fila = 1 chequeo.
      const params = { page: 1, limit: 9999, grouped: 'true', ...filters };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await inspectionService.getAll(params);
      const allRows = res.data.data || [];
      if (!allRows.length) { toast.info('No hay inspecciones para exportar.'); return; }
      exportToCSV(allRows);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Error al exportar. Intentá de nuevo.');
    } finally { setExporting(false); }
  }, [filters, debouncedSearch]);

  const clearSearch = () => { setSearch(''); setDebouncedSearch(''); setPage(1); };

  const cardClass = `rounded-2xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Inspecciones</h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{total} inspecciones registradas</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Botón PDF */}
          <motion.button
            onClick={handleExportPDF}
            disabled={exportingPDF}
            whileHover={!exportingPDF ? { scale: 1.03 } : {}}
            whileTap={!exportingPDF ? { scale: 0.97 } : {}}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all flex-shrink-0 ${
              darkMode
                ? 'bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-800'
                : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200'
            } ${exportingPDF ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {exportingPDF
              ? <><Loader className="w-4 h-4 animate-spin" />Generando...</>
              : <><FileDown className="w-4 h-4" />PDF</>
            }
          </motion.button>

          {/* Botón CSV */}
          <motion.button
            onClick={handleExport}
            disabled={exporting}
            whileHover={!exporting ? { scale: 1.03 } : {}}
            whileTap={!exporting ? { scale: 0.97 } : {}}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all flex-shrink-0 ${
              darkMode
                ? 'bg-green-900/30 hover:bg-green-900/50 text-green-400 border border-green-800'
                : 'bg-green-50 hover:bg-green-100 text-green-700 border border-green-200'
            } ${exporting ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            {exporting ? 'Exportando...' : 'CSV'}
          </motion.button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={BarChart2}     label="Total"                  value={stats.total_inspections || 0}      color="blue"   darkMode={darkMode} />
          <StatCard icon={CheckCircle2}  label="Completadas OK"         value={stats.completed_ok || 0}           color="green"  darkMode={darkMode} />
          <StatCard icon={TrendingUp}    label="Completadas c/ salteados" value={stats.completed_with_pending || 0} color="purple" darkMode={darkMode} />
          <StatCard icon={AlertTriangle} label="Incompletas"            value={stats.incomplete || 0}             color="yellow" darkMode={darkMode} />
        </div>
      )}

      {/* Filtros */}
      <div className={`${cardClass} p-4 space-y-3`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por piloto, avión o matrícula..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full pl-10 pr-10 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
            }`}
          />
          {/* ✅ Loading indicator mientras debouncea */}
          {search !== debouncedSearch && (
            <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-400" />
          )}
          {search === debouncedSearch && search && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
            className={`px-3 py-2 rounded-xl border text-sm focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
            <option value="">Todos los estados</option>
            <option value="completed">Completadas</option>
            <option value="completed_with_pending">Completadas c/ salteados</option>
            <option value="incomplete">Incompletas</option>
            <option value="in_progress">En curso</option>
          </select>
          <input type="date" value={filters.date_from}
            onChange={e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }}
            className={`px-3 py-2 rounded-xl border text-sm focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-gray-50 border-gray-200'}`} />
          <input type="date" value={filters.date_to}
            onChange={e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }}
            className={`px-3 py-2 rounded-xl border text-sm focus:outline-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-gray-50 border-gray-200'}`} />
          {(filters.status || filters.date_from || filters.date_to || search) && (
            <button onClick={() => { setFilters({ status: '', date_from: '', date_to: '' }); clearSearch(); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-xl">
              <X className="w-3.5 h-3.5" /> Limpiar todo
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : inspections.length === 0 ? (
        <div className={`${cardClass} text-center py-16`}>
          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No se encontraron inspecciones</p>
          {(search || filters.status || filters.date_from || filters.date_to) && (
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Probá cambiando los filtros de búsqueda</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {inspections.map((insp, i) => (
            <motion.div key={insp.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className={`${cardClass} overflow-hidden`}>
              <div className="flex items-start gap-4 p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${insp.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                  {insp.status === 'completed'
                    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : <AlertTriangle className="w-5 h-5 text-yellow-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{insp.manufacturer} {insp.model_name}</p>
                    {insp.registration && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{insp.registration}</span>
                    )}
                    <StatusBadge status={insp.status} />
                    {insp.phase_count > 1 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'}`}>
                        {insp.phase_count} fases
                      </span>
                    )}
                    <PhasesSummary phases={insp.phases} darkMode={darkMode} />
                  </div>
                  <div className={`flex flex-wrap gap-3 text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatInspectionDate(insp.inspection_date)}
                      {insp.inspection_time ? ` ${String(insp.inspection_time).slice(0, 5)}hs` : ''}
                    </span>
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{insp.inspector_name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                      <div className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                        style={{ width: `${insp.total_items ? (insp.checked_items / insp.total_items) * 100 : 0}%` }} />
                    </div>
                    <span className="text-xs text-gray-400">{insp.checked_items}/{insp.total_items}</span>
                  </div>
                </div>
                <div className={`flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {expandedId === insp.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              <AnimatePresence>
                {expandedId === insp.id && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}
                    className={`px-4 pb-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    {insp.phases?.some(p => p.remarks?.includes('Flow Mode')) ? (
                      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'16px 0' }}>
                        <span style={{ fontSize:20 }}>🌀</span>
                        <div>
                          <p style={{ margin:0, fontWeight:700, fontSize:13, color: darkMode?'white':'#0f172a' }}>Inspección completada con Flow Mode</p>
                          <p style={{ margin:'2px 0 0', fontSize:12, color: darkMode?'rgba(255,255,255,0.45)':'#64748b' }}>Ver estadísticas detalladas en el panel Flow Mode</p>
                        </div>
                      </div>
                    ) : insp.group_id ? (
                      // Contenedor (con o sin múltiples fases) — Sub-etapa 3.3
                      <GroupPhasesDetail groupId={insp.group_id} darkMode={darkMode} />
                    ) : (
                      // Inspección suelta (pre-3.0 o group_id NULL) — comportamiento idéntico al de siempre
                      <InspectionDetail id={insp.id} darkMode={darkMode} />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className={`px-4 py-2 rounded-xl font-medium text-sm ${page === 1 ? 'opacity-40 cursor-not-allowed' : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
            ← Anterior
          </button>
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Página {page} de {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
            className={`px-4 py-2 rounded-xl font-medium text-sm ${page === totalPages ? 'opacity-40 cursor-not-allowed' : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminInspections;
